'use client'

import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  PopoverBackdrop,
} from '@headlessui/react'
import clsx from 'clsx'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'

import logo from '/public/logo.png'
import logoIcon from '/public/icon-logo.png'
import Form from 'next/form'


type LinkItem = {
  name: string
  href: string
}

const menu: LinkItem[] = [
  {
    name: 'Deals',
    href: '/deals',
  },
  {
    name: 'Home & Garden',
    href: '/categories/home-and-garden',
  },
  {
    name: 'Reviews',
    href: '/reviews',
  },
  {
    name: 'Tech',
    href: '/categories/tech',
  },
  {
    name: 'Health & Lifestyle',
    href: '/categories/health-and-lifestyle',
  }
]


export default function Navbar() {

  const pathname = usePathname()

  function Logo() {
    return (
      <div className="flex shrink-0 items-center">
        <Link href="/" className="h-10 lg:hidden" prefetch={false}>
          <Image src={logoIcon} alt="ShopinHive Logo Icon" className="h-auto w-9"/>
        </Link>
        <Link href="/" className="hidden h-10 lg:block" prefetch={false}>
          <Image src={logo} alt="ShopinHive logo" className="h-10 w-auto" />
        </Link>
      </div>
    )
  }

  function DesktopNavigation() {
    return (
      <div className="ml-6 hidden items-center justify-between text-xl md:flex md:space-x-0.5 md:text-base lg:space-x-2">
        {menu.map((link, index) => (
          <DesktopNavItem key={`desktop-link-${index}`} link={link} />
        ))}
      </div>
    )
  }

  function HamburgerButton() {
    return (
      <PopoverButton
        className="group relative z-50 ml-4 flex cursor-pointer items-center justify-center rounded-full bg-gray-50 p-3 shadow-sm ring-1 ring-gray-900/5 transition duration-300 ease-in-out hover:bg-gray-100 focus:outline-none md:hidden"
        aria-label="Toggle Navigation"
      >
        <span className="relative h-3.5 w-4">
          <span className="absolute left-0 top-0 block h-0.5 w-full rotate-0 transform rounded-full bg-gray-600 opacity-100 transition-all duration-300 ease-in-out group-hover:bg-gray-900 group-data-[open]:left-1/2 group-data-[open]:top-1.5 group-data-[open]:w-0" />
          <span className="absolute left-0 top-1.5 block h-0.5 w-full transform rounded-full bg-gray-600 opacity-100 transition-all duration-300 ease-in-out group-hover:bg-gray-900 group-data-[open]:rotate-45" />
          <span className="absolute left-0 top-1.5 block h-0.5 w-full transform rounded-full bg-gray-600 opacity-100 transition-all duration-300 ease-in-out group-hover:bg-gray-900 group-data-[open]:-rotate-45" />
          <span className="absolute left-0 top-3 block h-0.5 w-full rotate-0 transform rounded-full bg-gray-600 opacity-100 transition-all duration-300 ease-in-out group-hover:bg-gray-900 group-data-[open]:left-1/2 group-data-[open]:top-1.5 group-data-[open]:w-0" />
        </span>
      </PopoverButton>
    )
  }

  function MobileMenu() {
    return (
      <>
        <PopoverBackdrop
          transition
          className="fixed inset-0 z-20 bg-slate-900 bg-opacity-50 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <PopoverPanel
          transition
          className="absolute inset-x-0 top-0 z-40 transform pt-20 data-[closed]:-translate-y-full data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        >
          {({ close }) => (
            <nav
              className="border-b border-gray-300/60 bg-white md:hidden"
              aria-label="Global"
              id="mobile-menu"
            >
              <div className="space-y-1 px-2 pb-3 pt-2">
                {menu.map((link, index) => (
                  <Link
                    key={`mobile-menu-link-${index}`}
                    prefetch={false}
                    href={link.href}
                    className={clsx(
                      'block rounded-lg px-4 py-3 font-medium',

                      pathname === link.href
                        ? 'bg-gray-50 text-amber-500'
                        : `text-gray-800 transition duration-300 ease-in-out hover:bg-gray-50 hover:text-amber-500`,
                    )}
                    onClick={() => {
                      close()
                    }}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>


            </nav>
          )}
        </PopoverPanel>
      </>
    )
  }

  function Search() {
    return (
      <div className="relative ml-4 h-10 w-full max-w-xxs rounded-3xl">
          <Form action="/search" className="group rounded-3xl transition duration-300 ease-in-out">
            <div className="absolute inset-y-0 left-3 flex items-center">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              name="query"
              type="search"
              className="h-10 w-full rounded-3xl border border-gray-200 bg-white px-10 py-3 text-sm leading-5 text-gray-800 transition duration-300 ease-in-out hover:bg-gray-50 focus:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-100"
              placeholder="Search..."
            />
          </Form>
      </div>
  )
  }

  function DesktopNavItem({
    link
  }: {
    link: LinkItem
  }) {
    const isActive = usePathname() === link.href

    return (
      <Link
        href={link.href}
        prefetch={false}
        className={clsx(
          'px-3 py-1 text-md font-medium',
          isActive
            ? 'text-amber-500'
            : 'text-gray-800 transition duration-300 ease-in-out hover:text-amber-500',
        )}
      >
        {link.name}
      </Link>
    )
  }

  return (
    <Popover as="header" className="relative">
      <div className="relative z-50 border-b border-gray-300/60">
        <nav className="mx-auto flex h-20 max-w-7xl items-center bg-white px-4 sm:px-6 lg:border-0 lg:px-8">
          {/* Main navbar for large screens */}
          <div className="flex w-full items-center justify-between">
            <Logo />

            <DesktopNavigation />
            <Search />
            <HamburgerButton />
          </div>
        </nav>
      </div>

      <MobileMenu />
    </Popover>
  )
}
